/**
 * MYAC-902 — shared display helpers for the QMS register tables (NC + CAPA).
 *
 * The extraction agents (MYAC-743 / MYAC-744) emit a `{ columns, rows }` table
 * that replicates the client's OWN register structure: each column carries a
 * semantic `role`, and `role` is the whole contract this display layer keys off.
 * NOTHING here hardcodes a column key, a label, or a status vocabulary — a
 * register whose columns we don't recognise still renders, just more plainly.
 *
 * Everything in this module is pure so it can be unit-tested without mounting.
 */

import type { DocumentHandle } from '@/models/DocumentHandle'

/**
 * Structural mirror of the NC / CAPA `DynamicColumn` models. Declared with
 * `role: string` (not a union) on purpose: the two models expose DIFFERENT role
 * unions (CAPA has `type`, NC has `origin` / `disposition` / `root_cause`), and
 * both must be assignable here. Unrecognised roles fall through to passthrough
 * handling, which is what keeps the shared components general.
 */
export interface RegisterColumn {
  key: string
  label: string
  role: string
  hint?: string | null
  source_header?: string | null
}

/** One register row: cell values keyed by `RegisterColumn.key`. */
export type RegisterRow = Record<string, unknown>

/** A row carrying the stable index the list/drawer use as a dataKey. */
export type IndexedRegisterRow = RegisterRow & { __idx: number }

/**
 * Where the files a row names live (MYAC-1642), keyed by file name: the register
 * store puts the projected `document_handles` under this key. Not a column, so,
 * like the supplier view's partner marker, search, facets and the export never see
 * it; it rides along the row's own spreads down to the drawer.
 */
export const DOCUMENT_HANDLES_KEY = '__document_handles'

/** The handle of the file `name` a row cites, or null when it did not resolve. */
export function documentHandleIn(row: RegisterRow | null | undefined, name: string): DocumentHandle | null {
  const handles = row?.[DOCUMENT_HANDLES_KEY] as Record<string, DocumentHandle> | undefined
  const key = name?.trim()
  // Own keys only: a cell reading « constructor » must not find Object's method.
  return key && handles && Object.prototype.hasOwnProperty.call(handles, key) ? handles[key]! : null
}

/**
 * Normalised status buckets. `other` is deliberately a first-class value rather
 * than a failure: extraction returns whatever the source spreadsheet used, so an
 * unrecognised status must render neutrally instead of being guessed into
 * open/closed (a register full of "under review" should not read as alarming).
 */
export type RegisterStatus = 'open' | 'closed' | 'other'

/** Deterministic, time-relative schedule bucket — recomputed on every render. */
export type RegisterTiming = 'past_due' | 'to_do' | 'done' | 'unknown'

/**
 * Value-normalise a cell for vocabulary matching: strip accents, lowercase, and
 * collapse everything non-alphanumeric to single spaces. So `"Clôturé "`,
 * `"CLOTURE"` and `"clôturé"` all reduce to `"cloture"`.
 */
export function normalizeValue(raw: unknown): string {
  return String(raw ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

// Status vocabularies, in de-accented normalised form (EN + FR, the two locales
// we ship). Kept deliberately CONSERVATIVE: only values that unambiguously mean
// "still live" or "finished" are mapped. Genuinely ambiguous states — "under
// review", "pending", "on hold" — stay `other` and render neutral rather than
// being forced into a bucket the source data doesn't support.
const OPEN_VALUES = new Set([
  'open',
  'opened',
  'ouvert',
  'ouverte',
  'in progress',
  'inprogress',
  'en cours',
  'ongoing',
  'wip',
  'active',
  'actif',
])
const CLOSED_VALUES = new Set([
  'closed',
  'close',
  'clos',
  'closed out',
  'clot',
  'cloture',
  'cloturee',
  'ferme',
  'fermee',
  'termine',
  'terminee',
  'done',
  'complete',
  'completed',
  'complet',
  'resolved',
  'resolu',
  'resolue',
  'solde',
  'soldee',
])

/** Map a raw status cell onto `open` / `closed` / `other` (never throws). */
export function normalizeStatus(raw: unknown): RegisterStatus {
  const v = normalizeValue(raw)
  if (!v) return 'other'
  if (OPEN_VALUES.has(v)) return 'open'
  if (CLOSED_VALUES.has(v)) return 'closed'
  return 'other'
}

/**
 * Display a cell value: drop a MIDNIGHT time component on date(-time) strings
 * ("2025-09-12 00:00:00" → "2025-09-12") while keeping a real time
 * ("2025-09-12 14:30:00" stays). Non-date values are returned unchanged.
 */
export function fmtCell(v: unknown): string {
  const s = v == null ? '' : String(v)
  return s.replace(/[ T]00:00:00(?:\.0+)?\s*$/, '')
}

/** Build a local-midnight Date, rejecting impossible calendar dates (Feb 31). */
function localDate(y: number, m: number, d: number): Date | null {
  if (m < 1 || m > 12 || d < 1 || d > 31) return null
  const dt = new Date(y, m - 1, d)
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null
  dt.setHours(0, 0, 0, 0)
  return dt
}

/**
 * LOOSE date parse — finds a date anywhere in the cell. This is what the
 * past-due maths has always used (the deadline column can read "2026-03-04
 * (revised)"), so the behaviour is preserved deliberately.
 */
export function parseRegisterDate(raw: unknown): Date | null {
  const s = fmtCell(raw).trim()
  let m = s.match(/(\d{4})[-./](\d{1,2})[-./](\d{1,2})/) // YYYY-MM-DD / YYYY.M.D
  if (m) return localDate(+m[1], +m[2], +m[3])
  m = s.match(/(\d{1,2})[-./](\d{1,2})[-./](\d{4})/) // DD/MM/YYYY
  if (m) return localDate(+m[3], +m[2], +m[1])
  return null
}

/**
 * STRICT date parse — the WHOLE cell must be a date (optionally with a time).
 * Used to decide whether an untyped passthrough column belongs on the record
 * timeline; the loose parse would happily promote "Batch 2026-03 rejected".
 */
/**
 * The time part of a date cell, in every shape the extracted registers actually
 * carry: a bare time, a `Z` instant, or an explicit offset (`+0200`, `-05:00`).
 *
 * MYAC-1013 follow-up — the offset form was missing, so `2024-04-05T12:02:08.872+0200`
 * did not parse as a date and fell through to being printed RAW. That is ~1/3 of
 * the date cells on dev, and it is what "les dates ne sont pas lisibles" meant.
 */
const TIME_PART = String.raw`(?:[ T][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?`

/**
 * A cell that is ENTIRELY one date, as a local `Date` at midnight.
 *
 * Deliberately takes the calendar date **as written** and never converts between
 * timezones: a register says "5 April" because a human wrote 5 April, and
 * re-interpreting that as an instant is how a date drifts to the 4th or the 6th
 * for a reader in another zone. Building from the literal Y/M/D means no timezone
 * is read, assumed or hardcoded anywhere — the value simply cannot shift.
 */
/** Compiled once: this runs per date cell, and a register is hundreds of rows. */
const YMD_RE = new RegExp(String.raw`^(\d{4})[-./](\d{1,2})[-./](\d{1,2})${TIME_PART}$`)
const DMY_RE = new RegExp(String.raw`^(\d{1,2})[-./](\d{1,2})[-./](\d{4})${TIME_PART}$`)

export function parseWholeDate(raw: unknown): Date | null {
  const s = fmtCell(raw).trim()
  let m = s.match(YMD_RE)
  if (m) return localDate(+m[1], +m[2], +m[3])
  m = s.match(DMY_RE)
  if (m) return localDate(+m[3], +m[2], +m[1])
  return null
}

/**
 * How every register surface renders a date: `DD/MM/YYYY`, no time, ever
 * (MYAC-1013 follow-up, Charles: "limiter le display au jour, pas besoin de
 * détails sur l'heure").
 *
 * Written out rather than handed to `Intl` / vue-i18n's `d()` **on purpose**. A
 * numeric date is locale-ORDERED: the same 5 April renders `05/04/2024` under
 * `fr` and `04/05/2024` under a bare `en`, which Intl resolves US-style. On a
 * compliance register — detection dates, CAPA deadlines — that is a silent
 * misreading, and both orderings look equally valid to whoever is reading. One
 * fixed day-first order removes the ambiguity for every locale the app can run
 * under, and the app is French-first.
 *
 * Reads only the LOCAL parts of a `Date` that `parseWholeDate` already pinned to
 * local midnight, so no timezone is involved here either.
 */
export function formatRegisterDate(date: Date): string {
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}/${date.getFullYear()}`
}

/**
 * A cell for display: a whole date becomes a day-only string, anything else is
 * returned as-is. This is the ONE place that decides "is this cell a date?", so
 * a row, a drawer field and a timeline step can never disagree.
 */
export function formatCellForDisplay(raw: unknown): string {
  const asDate = parseWholeDate(raw)
  return asDate ? formatRegisterDate(asDate) : fmtCell(raw)
}

/** Today at local midnight — the reference point for every schedule computation. */
export function startOfToday(now: Date = new Date()): Date {
  const d = new Date(now.getTime())
  d.setHours(0, 0, 0, 0)
  return d
}

/** Whole days from `from` to `to` (negative when `to` is in the past). */
export function dayDiff(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 86_400_000)
}

/**
 * Deterministic timing bucket from the columns the AGENT marked: role `status`
 * + role `date_end` (the deadline). The agent makes the semantic pick; we only
 * compare to today, so a `to_do` becomes `past_due` over time with no
 * re-extraction. Semantics match the pre-MYAC-902 filter exactly — a status we
 * can't place is `unknown` rather than assumed open.
 */
export function rowTiming(
  row: RegisterRow,
  statusKey: string | undefined,
  deadlineKey: string | undefined,
  today: Date = startOfToday(),
): RegisterTiming {
  const status = statusKey ? normalizeStatus(row[statusKey]) : 'other'
  if (status === 'closed') return 'done'
  if (status !== 'open') return 'unknown'
  const d = deadlineKey ? parseRegisterDate(row[deadlineKey]) : null
  if (!d) return 'unknown'
  return d.getTime() < today.getTime() ? 'past_due' : 'to_do'
}

/**
 * Lateness for the row/drawer chip — the ONE thing in the design that shouts.
 * Red past due, amber when the deadline is close, nothing otherwise.
 */
export interface RegisterLateness {
  kind: 'past_due' | 'due_soon'
  days: number
}

/** A deadline this many days out or nearer earns the amber "due in Nd" chip. */
export const DUE_SOON_DAYS = 30

/**
 * Deliberately NOT built on `rowTiming`: a record whose status we can't place
 * ("under review") still has a live deadline, and hiding its lateness would be
 * the one thing this display must never do. Only a CLOSED record is exempt.
 * `rowTiming`'s `unknown` bucket exists for the filter, not for the chip.
 */
export function rowLateness(
  row: RegisterRow,
  statusKey: string | undefined,
  deadlineKey: string | undefined,
  today: Date = startOfToday(),
): RegisterLateness | null {
  if (statusKey && normalizeStatus(row[statusKey]) === 'closed') return null
  const d = deadlineKey ? parseRegisterDate(row[deadlineKey]) : null
  if (!d) return null
  const days = dayDiff(today, d)
  if (days < 0) return { kind: 'past_due', days: -days }
  return days <= DUE_SOON_DAYS ? { kind: 'due_soon', days } : null
}

/**
 * Character budget for the row summary. Every row truncates to the SAME budget
 * so the list keeps one uniform row height however verbose the source
 * spreadsheet was; the full text lives in the drawer behind "View more".
 */
export const SUMMARY_BUDGET = 120

/** Drop trailing whitespace and dangling punctuation before the ellipsis. */
function trimTail(s: string): string {
  return s.replace(/[\s.,;:–—-]+$/, '')
}

/** Truncate on a word boundary within `budget`, reporting whether it clipped. */
export function truncateSummary(
  raw: unknown,
  budget: number = SUMMARY_BUDGET,
): { text: string; truncated: boolean } {
  const s = fmtCell(raw).replace(/\s+/g, ' ').trim()
  if (s.length <= budget) return { text: s, truncated: false }
  const cut = s.slice(0, budget)
  // A word that ends exactly ON the budget is already a clean boundary — don't
  // throw it away looking for an earlier space.
  if (/\s/.test(s[budget])) return { text: trimTail(cut), truncated: true }
  const lastSpace = cut.lastIndexOf(' ')
  // Only fall back to a hard cut when there is no space in the last third —
  // otherwise a single very long token would collapse the summary to nothing.
  return { text: trimTail(lastSpace > budget * 0.6 ? cut.slice(0, lastSpace) : cut), truncated: true }
}

/**
 * Split a multi-value cell (source filenames, linked references) into a list.
 * Registers use newlines, semicolons and commas interchangeably.
 */
export function splitList(raw: unknown): string[] {
  return fmtCell(raw)
    .split(/\s*[\n;,]\s*/)
    .map((s) => s.trim())
    .filter(Boolean)
}

/**
 * Does a passthrough column hold cross-references to the sibling register?
 *
 * The `{columns, rows}` contract has no `link` role today, so linked references
 * arrive as an ordinary passthrough column ("Linked CAPA", "CAPA ref",
 * `linked_capa_references`). We detect them by key/label/source_header — and we
 * also honour an explicit `link` role, so the day extraction emits one this
 * heuristic simply stops being the path that fires. Display-layer only: a false
 * negative just means no chip.
 */
// A word that states a RELATIONSHIP is enough on its own ("Linked records"):
// which register the values point at is settled per value by the resolver, not by
// the header. A bare reference word is weaker — "Reference" is usually a second
// copy of the identity — so it additionally has to name a register ("NC ref").
const LINK_RELATION =
  /(?:^|[^a-z])(?:linked|link|related|associated|assoc|lie|lies|liee|liees|associe|associes|associee|associees|rattache|rattachee|rattachees)(?:[^a-z]|$)/
const LINK_REF_WORD = /(?:^|[^a-z])(?:ref|refs|reference|references)(?:[^a-z]|$)/
const LINK_REGISTER = /(?:^|[^a-z])(?:capa|nc|ncr|fnc|non ?conformit\w*|ca ?pa)(?:[^a-z]|$)/

export function isLinkColumn(col: RegisterColumn): boolean {
  if (col.role === 'link') return true
  if (col.role !== 'passthrough') return false
  const haystack = normalizeValue(`${col.key} ${col.label} ${col.source_header ?? ''}`)
  if (LINK_RELATION.test(haystack)) return true
  return LINK_REF_WORD.test(haystack) && LINK_REGISTER.test(haystack)
}

/** Which register a reference points at. */
export type RegisterTarget = 'nc' | 'capa'

/**
 * Resolve a linked reference to the register it points at. Returns null when
 * nothing places it, which means "don't offer a jump" rather than "guess".
 *
 * Resolution only ever uses evidence about the REFERENCE ITSELF — never the
 * column it sits in. A link column is routinely mixed (`CAPA-21-01`, `NC-8` and
 * `DOC2UPRD-1693` side by side), so a header like "Linked CAPA" is a blanket
 * claim that is wrong for most of its own values.
 */
export type ReferenceResolver = (reference: string) => RegisterTarget | null

// ---------------------------------------------------------------------------
// Layer 2 — a register marker inside the reference.
//
// Specific to the value, and right for house-style references. It cannot place a
// client scheme ("KAR03/AC01"), which is what the identity lookup above is for.
// ---------------------------------------------------------------------------

// A marker token must match one of these EXACTLY. Matching loosely would read
// "NCP-5" as an NC and "CAPEX-2" as a CAPA.
const NC_MARKERS = new Set(['nc', 'ncr', 'fnc', 'nonconformity', 'nonconformite'])
const CAPA_MARKERS = new Set(['capa'])

/**
 * Look for a register marker among the reference's tokens.
 *
 * Scans EVERY token rather than just the leading one, because real registers put
 * the marker in the middle: `21-NC-0001` is year-first, so a leading-letters rule
 * finds nothing and the reference would only resolve while its register happened
 * to be loaded. `DOC2UPRD-1693` still yields nothing, which is what we want.
 */
export function referenceMarkerTarget(reference: string): RegisterTarget | null {
  for (const token of normalizeValue(reference).split(' ')) {
    // The marker may be glued to its number ("NC2026-014" → token "nc2026"), so
    // strip a purely trailing number. "doc2uprd" has letters AFTER its digit and
    // so is tested whole, which is why it stays unplaceable.
    const marker = token.match(/^([a-z]+)\d*$/)?.[1] ?? token
    if (CAPA_MARKERS.has(marker)) return 'capa'
    if (NC_MARKERS.has(marker)) return 'nc'
  }
  return null
}

// ---------------------------------------------------------------------------
// Layer 1 — look the reference up in the registers' own identity values.
//
// AUTHORITATIVE and completely vocabulary-free: if `KAR03/AC01` is an identity in
// the CAPA register, the reference is a CAPA. This is the only layer that can
// place a client-specific reference scheme, which is why it goes first.
// ---------------------------------------------------------------------------

/** Every non-empty value of a table's identity column. */
export function identityValues(columns: RegisterColumn[], rows: RegisterRow[]): string[] {
  const col = columnByRole(columns, 'identity')
  return col ? distinctValues(rows, col.key) : []
}

/**
 * Indexable pieces of a reference: the whole thing, plus each SEGMENT when it is
 * a composite. `KAR03/AC01` yields `kar03 ac01`, `kar03` and `ac01`, so a link
 * citing either half still finds its record.
 *
 * Only `/ \ |` separate segments. A hyphen is INTERNAL to a single reference —
 * splitting on it would shred `CAPA-21-01` into `capa`, `21` and `01` and index
 * those meaningless fragments, so a stray "01" anywhere would resolve to CAPA.
 */
const SEGMENT_SEPARATOR = /[/\\|]/

export function referenceTokens(reference: string): string[] {
  const segments = String(reference ?? '')
    .split(SEGMENT_SEPARATOR)
    .map(normalizeValue)
    .filter(Boolean)
  if (!segments.length) return []
  const full = segments.join(' ')
  return segments.length > 1 ? [full, ...segments] : [full]
}

/** One register's contribution to the reference index. */
export interface ReferenceSource {
  target: RegisterTarget
  columns: RegisterColumn[]
  rows: RegisterRow[]
}

/**
 * Build the layered resolver: an identity match in one of the registers, else the
 * reference's own prefix. Both layers judge the REFERENCE; nothing here consults
 * the column it came from, because link columns are routinely mixed.
 *
 * Ambiguity is never resolved by guessing — a reference that is an identity in
 * BOTH registers, or a segment occurring in both, yields null so the value
 * renders as a plain chip instead of a jump to a coin-flip destination.
 */
export function buildReferenceResolver(sources: ReferenceSource[]): ReferenceResolver {
  const exact = new Map<string, Set<RegisterTarget>>()
  const segments = new Map<string, Set<RegisterTarget>>()

  for (const src of sources) {
    for (const identity of identityValues(src.columns, src.rows)) {
      const [full, ...rest] = referenceTokens(identity)
      if (!full) continue
      add(exact, full, src.target)
      for (const segment of rest) add(segments, segment, src.target)
    }
  }

  return (reference: string) => {
    const tokens = referenceTokens(reference)
    // A whole-reference identity match is the strongest signal there is.
    for (const token of tokens) {
      const hit = only(exact.get(token))
      if (hit) return hit
    }
    // A marker in the reference outranks a segment match: `NC-8` says NC
    // outright, while a segment could collide with an unrelated record.
    const marker = referenceMarkerTarget(reference)
    if (marker) return marker
    for (const token of tokens) {
      const hit = only(segments.get(token))
      if (hit) return hit
    }
    return null
  }
}

function add(map: Map<string, Set<RegisterTarget>>, key: string, target: RegisterTarget): void {
  const set = map.get(key)
  if (set) set.add(target)
  else map.set(key, new Set([target]))
}

/** The single member of a set, or null when absent or ambiguous. */
function only(set: Set<RegisterTarget> | undefined): RegisterTarget | null {
  return set?.size === 1 ? [...set][0] : null
}

/**
 * The default resolver for a list rendered without cross-register context: no
 * identity index, so only a marker inside the reference itself applies.
 */
export const resolveByMarkerOnly: ReferenceResolver = referenceMarkerTarget

/** A reference cell value plus where (if anywhere) it can be followed to. */
export interface LinkedReference {
  reference: string
  target: RegisterTarget | null
}

/** Every reference across a row's link columns, each with its resolved target. */
export function linkedReferences(
  cols: RegisterColumn[],
  row: RegisterRow,
  resolve: ReferenceResolver = resolveByMarkerOnly,
): LinkedReference[] {
  return cols.flatMap((c) =>
    splitList(row[c.key]).map((reference) => ({ reference, target: resolve(reference) })),
  )
}

/**
 * The link columns for a table — `isLinkColumn` narrowed by the DATA.
 *
 * The name heuristic alone matches a column like "Related ref" whose values turn
 * out to be document ids, which then renders a row of chips that can't be
 * followed. Requiring at least one RESOLVABLE reference somewhere in the column
 * keeps such a column an ordinary passthrough, so its values stay visible in the
 * drawer's folded fields instead of becoming dead affordances. An explicit `link`
 * role is trusted without this check.
 */
export function linkColumns(
  columns: RegisterColumn[],
  rows: RegisterRow[],
  resolve: ReferenceResolver = resolveByMarkerOnly,
): RegisterColumn[] {
  return columns.filter((c) => {
    if (c.role === 'link') return true
    if (!isLinkColumn(c)) return false
    return rows.some((r) => splitList(r[c.key]).some((ref) => resolve(ref) !== null))
  })
}

/**
 * Roles that belong on the quiet meta line under the summary, in reading order.
 * `type` covers CAPA (corrective/preventive); `origin` / `disposition` cover NC.
 *
 * MYAC-1013 — the supplier and change-control registers joined the shared record
 * list, and they carry their own roles: `nature` / `criticality` /
 * `quality_agreement` for suppliers, `requester` for changes. Without them a
 * supplier row rendered as a bare name, because every fact it holds sits in a role
 * this list didn't know. Dates stay first: they read the same in every register.
 * Purely additive for NC and CAPA, which carry none of the new roles.
 */
export const META_ROLES = [
  'date_start',
  'date_end',
  'origin',
  'disposition',
  'type',
  'nature',
  'criticality',
  'quality_agreement',
  'requester',
] as const

/** Meta entries are capped so a wide register can't stretch the row. */
export const META_LIMIT = 4

/**
 * Roles a low-cardinality facet filter may be built from, in preference order.
 * `nature` (supplier / customer / subcontractor) and `criticality` are the two
 * facets a supplier register is actually browsed by; `requester` is the change
 * register's equivalent. The bounds below still decide whether one appears.
 */
export const FACET_ROLES = [
  'origin',
  'disposition',
  'type',
  'nature',
  'criticality',
  'requester',
] as const

/** A facet is only useful between these bounds — 1 value filters nothing. */
export const FACET_MIN_VALUES = 2
export const FACET_MAX_VALUES = 25

/** First column matching `role`, or undefined. */
export function columnByRole(
  columns: RegisterColumn[],
  role: string,
): RegisterColumn | undefined {
  return columns.find((c) => c.role === role)
}

/** All columns matching `role`, in declared order. */
export function columnsByRole(columns: RegisterColumn[], role: string): RegisterColumn[] {
  return columns.filter((c) => c.role === role)
}

/** Distinct non-empty raw values for a column, in first-seen order. */
export function distinctValues(rows: RegisterRow[], key: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const r of rows) {
    const v = fmtCell(r[key]).trim()
    if (!v) continue
    const norm = normalizeValue(v)
    if (seen.has(norm)) continue
    seen.add(norm)
    out.push(v)
  }
  return out
}

/**
 * Timeline steps for the drawer: every date-bearing column, chronologically.
 *
 * Roles `date_start` / `date_end` always qualify. Untyped passthrough columns
 * join only when they're CONVINCINGLY dates — most non-empty values parse as a
 * whole date — which is what turns six date rows into one timeline without
 * hardcoding a single column name. Undated steps sort last, in column order.
 */
export const DATE_COLUMN_RATIO = 0.6

export function dateColumns(columns: RegisterColumn[], rows: RegisterRow[]): RegisterColumn[] {
  return columns.filter((c) => {
    if (c.role === 'date_start' || c.role === 'date_end') return true
    if (c.role !== 'passthrough') return false
    let filled = 0
    let dated = 0
    for (const r of rows) {
      if (!fmtCell(r[c.key]).trim()) continue
      filled++
      if (parseWholeDate(r[c.key])) dated++
    }
    return filled > 0 && dated / filled >= DATE_COLUMN_RATIO
  })
}

export interface TimelineStep {
  key: string
  label: string
  raw: string
  date: Date | null
  /** `done` = on or before today, `next` = the nearest future date, else neither. */
  state: 'done' | 'next' | 'pending'
}

export function timelineSteps(
  cols: RegisterColumn[],
  row: RegisterRow,
  today: Date = startOfToday(),
): TimelineStep[] {
  const steps = cols.map((c, i) => {
    const raw = fmtCell(row[c.key]).trim()
    return { key: c.key, label: c.label, raw, date: parseRegisterDate(raw), order: i }
  })
  steps.sort((a, b) => {
    if (a.date && b.date) return a.date.getTime() - b.date.getTime() || a.order - b.order
    if (a.date) return -1
    if (b.date) return 1
    return a.order - b.order
  })
  // Exactly one step is "next": the earliest date still ahead of today. Anything
  // dated on or before today is done; the rest are plain pending.
  const nextIdx = steps.findIndex((s) => s.date && s.date.getTime() > today.getTime())
  return steps.map((s, i) => ({
    key: s.key,
    label: s.label,
    raw: s.raw,
    date: s.date,
    state: s.date && s.date.getTime() <= today.getTime() ? 'done' : i === nextIdx ? 'next' : 'pending',
  }))
}

/**
 * A value this long that appears verbatim inside something already shown is an
 * echo, not new information. The floor keeps short codes — which legitimately
 * recur inside prose — from being suppressed.
 */
export const ECHO_MIN_LENGTH = 20

/**
 * The folded "other extracted fields" list: passthrough columns that aren't
 * already surfaced, MINUS any whose value merely repeats one shown above.
 * Suppressing those duplicates is what kills the "wall of text, some of it
 * repeated". Three kinds of repeat are caught:
 *
 *  - exact: the same value under a second header;
 *  - echo: a long value quoted verbatim inside a shown one (a `Comment` column
 *    holding the first sentence of the description);
 *  - translated status: a `Statut: ouvert` beside `Status: open` — the same
 *    status bucket, so the second header adds nothing.
 */
export function otherFields(
  columns: RegisterColumn[],
  row: RegisterRow,
  shownKeys: Set<string>,
  statusKey?: string,
): RegisterColumn[] {
  const shownValues = new Set<string>()
  for (const c of columns) {
    if (!shownKeys.has(c.key)) continue
    const v = normalizeValue(row[c.key])
    if (v) shownValues.add(v)
  }
  const shownList = [...shownValues]
  // Only a PLACEABLE bucket dedupes: two unrecognised status-ish values both
  // normalise to `other`, which says nothing about them being the same.
  const shownStatus = statusKey && shownKeys.has(statusKey) ? normalizeStatus(row[statusKey]) : 'other'

  return columns.filter((c) => {
    if (shownKeys.has(c.key)) return false
    const v = normalizeValue(row[c.key])
    if (!v) return false
    if (shownValues.has(v)) return false
    if (shownStatus !== 'other' && normalizeStatus(v) === shownStatus) return false
    if (v.length >= ECHO_MIN_LENGTH && shownList.some((sv) => sv.includes(v))) return false
    return true
  })
}
